const TZ = 'Europe/Stockholm';

export const MONTHS = Object.freeze([
    'januari',
    'februari',
    'mars',
    'april',
    'maj',
    'juni',
    'juli',
    'augusti',
    'september',
    'oktober',
    'november',
    'december',
]);

export const MONTHS_SHORT = Object.freeze([
    'jan',
    'feb',
    'mar',
    'apr',
    'maj',
    'jun',
    'jul',
    'aug',
    'sep',
    'okt',
    'nov',
    'dec',
]);

export const WEEEKDAYS = Object.freeze([
    'söndag',
    'måndag',
    'tisdag',
    'onsdag',
    'torsdag',
    'fredag',
    'lördag',
]);

export const WEEEKDAYS_SHORT = Object.freeze(['mån', 'tis', 'ons', 'tor', 'fre', 'lör', 'sön']);

// eslint-disable-next-line no-underscore-dangle
function _pad(num, size) {
    return `000000000${num}`.substr(-size);
}

export const convertToTimezone = (date: Date): Date => {
    // Try catch is due to IE11 not supporting timezone
    try {
        if (typeof window !== 'undefined') {
            return new Date(
                (typeof date === 'string' ? new Date(date) : date).toLocaleString('en-US', { timeZone: TZ })
            );
        }
    } catch {} // eslint-disable-line

    return typeof date === 'string' ? new Date(date) : date;
};

export const getIsoDate = (date: Date): string => {
    const current = convertToTimezone(date);

    return `${current.getFullYear()}-${_pad(current.getMonth() + 1, 2)}-${_pad(current.getDate(), 2)}`;
};

export const addDate = (days: number): Date => {
    const modifiedDate = new Date().getTime() + days * 86400 * 1000;

    return convertToTimezone(new Date(modifiedDate));
};

export const modifyDate = (date: Date, days: number): Date => {
    const modifiedDate = new Date(date).getTime() + days * 86400 * 1000;

    return convertToTimezone(new Date(modifiedDate));
};

const setTime = (date: string): string => `${date}T00:00:00Z`;

const splitSeconds = (seconds) => {
    const secondsPart = seconds % 60;
    const minutePart = Math.floor((seconds % 3600) / 60);
    const hourPart = Math.floor(seconds / 3600);

    return {
        seconds: secondsPart,
        minutes: minutePart,
        hours: hourPart,
    };
};

export const ucFirst = (string: string): string => string.charAt(0).toUpperCase() + string.slice(1);

export const convertIsoDateToDate = (date: string | Date): Date => {
    if (typeof date === 'string' && date.length === 10) {
        return new Date(setTime(date));
    }

    return new Date(date);
};

export const isSameDay = (dateOne: Date, dateTwo: Date | string): boolean =>
    getIsoDate(convertToTimezone(convertIsoDateToDate(dateOne))) ===
    getIsoDate(convertToTimezone(convertIsoDateToDate(dateTwo)));

export const isBefore = (date: Date, isBeforeDate: Date): boolean =>
    convertToTimezone(convertIsoDateToDate(date)).getTime() <
    convertToTimezone(convertIsoDateToDate(isBeforeDate)).getTime();

export const isAfter = (date: Date, isAfterDate: Date): boolean =>
    convertToTimezone(convertIsoDateToDate(date)).getTime() >
    convertToTimezone(convertIsoDateToDate(isAfterDate)).getTime();

export const formatCalendarDate = (date: string | Date): string => {
    const convertedDate = convertToTimezone(convertIsoDateToDate(date));

    // Date objects
    const todayDate = addDate(0);
    const tomorrowDate = addDate(1);
    const yesterdayDate = addDate(-1);
    const day = convertedDate.getDate();
    const month = MONTHS_SHORT[convertedDate.getMonth()];
    const weekday = WEEEKDAYS[convertedDate.getDay()];

    if (isSameDay(convertedDate, todayDate)) {
        return `Idag ${day} ${month}`;
    }
    if (isSameDay(convertedDate, yesterdayDate)) {
        return `Igår ${day} ${month}`;
    }
    if (isSameDay(convertedDate, tomorrowDate)) {
        return `Imorgon ${day} ${month}`;
    }

    return `${ucFirst(weekday)} ${day} ${month}`;
};

export const formatDuration = (seconds: number, isPositionLink = false): string => {
    // For Play buttons with very short video durations
    if (isPositionLink) {
        if (seconds === 0) {
            return null;
        }

        if (seconds < 60) {
            return `00:${seconds.toLocaleString('sv-SE', {
                minimumIntegerDigits: 2,
            })}`;
        }
    }

    if (!seconds || seconds <= 0) {
        return '0 sek';
    }

    const format = [];

    // Hide seconds after 5 minutes
    if (seconds > 300) {
        const mod = seconds % 60;
        // eslint-disable-next-line no-param-reassign
        seconds = mod >= 30 ? seconds + (60 - mod) : seconds - mod;
    }

    const duration = splitSeconds(seconds);

    if (duration.hours > 0) {
        // Hide minutes after 8 hours
        if (duration.hours >= 8) {
            duration.hours = duration.minutes >= 30 ? duration.hours + 1 : duration.hours;
            duration.minutes = 0;
        }

        format.push(duration.hours);
        format.push('tim');
    }

    if (duration.minutes > 0) {
        format.push(duration.minutes);
        format.push('min');
    }

    if (duration.seconds > 0) {
        format.push(duration.seconds);
        format.push('sek');
    }

    return format.join(' ');
};
